import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatPaise } from '../../lib/format.js';

export interface RefundDialogProps {
  orderNumber: string;
  remainingRefundablePaise: number;
  onClose: () => void;
  onSubmit: (input: { amountPaise?: number; reason?: string }) => void;
  isSubmitting: boolean;
}

/** ADMIN-only refund initiation. Defaults to a full refund of whatever remains unrefunded (omitting `amountPaise` entirely, matching the backend's own "omit for a full refund" contract) -- a partial amount is opt-in via the checkbox, never the default, since a full refund is the common case and a partial one is a deliberate exception. */
export function RefundDialog({ orderNumber, remainingRefundablePaise, onClose, onSubmit, isSubmitting }: RefundDialogProps) {
  const [partial, setPartial] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const amountId = useId();
  const reasonId = useId();

  useEffect(() => {
    confirmButtonRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleConfirm() {
    if (partial) {
      const rupees = Number(amount);
      if (!Number.isFinite(rupees) || rupees <= 0) {
        setError('Enter a positive refund amount.');
        return;
      }
      const paise = Math.round(rupees * 100);
      if (paise > remainingRefundablePaise) {
        setError(`Amount exceeds the refundable balance of ${formatPaise(remainingRefundablePaise)}.`);
        return;
      }
      setError(null);
      onSubmit({ amountPaise: paise, reason: reason.trim() || undefined });
      return;
    }
    setError(null);
    onSubmit({ reason: reason.trim() || undefined });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-base font-semibold text-gray-900">Refund — {orderNumber}</h2>
        <p className="mt-1 text-sm text-gray-500">
          Refundable balance: {formatPaise(remainingRefundablePaise)}.
        </p>

        <div className="mt-4 space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={partial}
              onChange={(event) => setPartial(event.target.checked)}
              className="rounded border-gray-300"
            />
            Partial refund
          </label>
          {partial && (
            <div>
              <label htmlFor={amountId} className="mb-1 block text-xs font-medium text-gray-600">
                Amount (₹)
              </label>
              <input
                id={amountId}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
              />
            </div>
          )}
          <div>
            <label htmlFor={reasonId} className="mb-1 block text-xs font-medium text-gray-600">
              Reason (optional)
            </label>
            <textarea
              id={reasonId}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
          >
            {isSubmitting ? 'Processing…' : partial ? 'Refund partial amount' : 'Refund full balance'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
